import styles from './select.module.css';

import { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { COLORS } from '../../config';
import SelectDropdown from './SelectDropdown';
import SelectValue from './SelectValue';
import SelectInput from './SelectInput';
import useOutsideClick from '../../hooks/useOutsideClick';
import attentionIcon from 'assets/svg/attention-01.svg';

/**
 * **Select** — это универсальный компонент выпадающего списка, поддерживающий различные режимы выбора,
 * поиск и кастомизацию отображения. Компонент предоставляет широкие возможности для настройки
 * и обработки выбора пользователя.
 *
 * ### Описание параметров:
 *
 * @param {Object[]} options - Массив опций для выбора. Каждая опция должна быть объектом.
 * @param {*} [value] - Текущее выбранное значение или массив значений (для multiple=true).
 * @param {function} [onChange] - Callback-функция, вызываемая при изменении выбранного значения.
 * @param {string} [placeholder='Не выбрано'] - Текст для отображения, когда ничего не выбрано.
 * @param {boolean} [disabled=false] - Флаг, определяющий, отключен ли компонент.
 * @param {boolean} [multiple=false] - Режим множественного выбора. При включении позволяет выбрать несколько опций.
 * @param {boolean} [clearable=true] - Возможность очистить выбранное значение.
 * @param {boolean} [searchable=false] - Включение поиска по опциям.
 * @param {string} [valueKey='id'] - Имя поля в объекте options, которое используется как значение опции.
 * @param {string} [titleKey='name'] - Имя поля в объекте options, которое используется как текст опции.
 * @param {string[]} [searchFields=[]] - Массив полей для поиска (если не указан, используется только titleKey).
 * @param {function} [renderOption] - Функция для кастомного рендеринга опций.
 *    Принимает параметры: { option, isSelected }
 * @param {function} [renderValue] - Функция для кастомного рендеринга выбранного значения.
 *    Принимает параметры: { value, options, valueKey, titleKey }
 * @param {string} [className] - Дополнительные CSS-классы для контейнера.
 * @param {string} [label] - Текст метки (лейбла) для селекта.
 * @param {string} [arrowColor=COLORS.text_secondary] - Цвет стрелки раскрытия списка.
 * @param {string} [error] - Текст ошибки для отображения под компонентом.
 *
 * @component
 * @example
 * const options = [
 *   { id: 1, name: 'Опция 1', code: 'opt1' },
 *   { id: 2, name: 'Опция 2', code: 'opt2' },
 * ];
 *
 * // Простое использование
 * <Select
 *   options={options}
 *   value={selectedValue}
 *   onChange={handleChange}
 *   titleKey="name"
 *   valueKey="id"
 *   notSelectedText="Не выбрано"
 * />
 *
 * // С возможностью поиска
 * <Select
 *   options={options}
 *   value={selectedValue}
 *   onChange={handleChange}
 *   titleKey="name"
 *   valueKey="id"
 *   searchable={true}
 *   notSelectedText="Не выбрано"
 * />
 *
 * // Множественный выбор
 * <Select
 *   options={options}
 *   value={selectedValues}
 *   onChange={handleChange}
 *   titleKey="name"
 *   valueKey="id"
 *   multiple={true}
 *   notSelectedText="Не выбрано"
 * />
 *
 * // С кастомным рендером опций
 * <Select
 *   options={options}
 *   value={selectedValue}
 *   onChange={handleChange}
 *   titleKey="name"
 *   valueKey="id"
 *   notSelectedText="Не выбрано"
 *   renderOption={({ option, isSelected }) => (
 *     <div className={isSelected ? 'selected' : ''}>
 *       <span>{option.name}</span>
 *       <span className="code">{option.code}</span>
 *     </div>
 *   )}
 * />
 */
const Select = ({
  options = [],
  value,
  onChange,
  placeholder = 'Не выбрано',
  disabled = false,
  multiple = false,
  clearable = true,
  searchable = false,
  valueKey = 'id',
  titleKey = 'name',
  searchFields = [],
  renderOption,
  renderValue,
  className,
  label,
  arrowColor = COLORS.text_secondary,
  error,
  hideErrorBlock = false,
  errorMessage = undefined,
  required = false,
  isFormValidate,
  ...props
}) => {
  const blockErrorRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  const [selectedValues, setSelectedValues] = useState([]);
  const [searchValue, setSearchValue] = useState('');
  const [isSearchMode, setIsSearchMode] = useState(false);
  const selectRef = useRef(null);
  const [position, setPosition] = useState({ top: 0, left: 0, width: 0 });

  // Устанавливаем выбранные значения при изменении value извне
  useEffect(() => {
    if (multiple) {
      setSelectedValues(Array.isArray(value) ? value : []);
    } else {
      setSelectedValues(value !== undefined && value !== null ? [value] : []);
    }
  }, [value, multiple]);

  useEffect(() => {
    if (!isOpen) return;

    const calculatePosition = () => {
      if (selectRef.current) {
        const rect = selectRef.current.getBoundingClientRect();
        setPosition({
          top: rect.bottom - (blockErrorRef?.current?.offsetHeight || 2),
          left: rect.left,
          width: rect.width,
        });
      }
    };

    calculatePosition();

    window.addEventListener('scroll', calculatePosition, true);
    window.addEventListener('resize', calculatePosition);

    return () => {
      window.removeEventListener('scroll', calculatePosition, true);
      window.removeEventListener('resize', calculatePosition);
    };
  }, [isOpen, hideErrorBlock]);

  const handleClickOutside = (event) => {
    setIsOpen(false);
    setSearchValue('');
    setIsSearchMode(false);
  };

  // Обработка клика вне компонента
  useOutsideClick(selectRef, handleClickOutside);

  // Фильтрация опций по поисковому запросу
  const filteredOptions = useMemo(() => {
    if (!searchable || !searchValue.trim()) {
      return options;
    }

    const searchLower = searchValue.toLowerCase();
    const fieldsToSearch = searchFields.length > 0 ? searchFields : [titleKey];

    return options.filter((option) => {
      return fieldsToSearch.some((field) => {
        const fieldValue = option[field];
        return (
          fieldValue !== undefined &&
          fieldValue !== null &&
          String(fieldValue).toLowerCase().includes(searchLower)
        );
      });
    });
  }, [options, searchValue, searchable, searchFields, titleKey]);

  // Переключение состояния выпадающего меню
  const toggleDropdown = () => {
    if (!disabled) {
      const newIsOpen = !isOpen;
      setIsOpen(newIsOpen);

      if (!newIsOpen) {
        setSearchValue('');
        setIsSearchMode(false);
      } else if (searchable) {
        setIsSearchMode(true);
      }
    }
  };

  // Обработка выбора опции
  const handleOptionClick = (optionValue) => {
    let newValues;

    if (multiple) {
      if (selectedValues.includes(optionValue)) {
        newValues = selectedValues.filter((val) => val !== optionValue);
      } else {
        newValues = [...selectedValues, optionValue];
      }
    } else {
      newValues = [optionValue];
      setIsOpen(false);
      setSearchValue('');
      setIsSearchMode(false);
    }

    setSelectedValues(newValues);

    if (onChange) {
      onChange(multiple ? newValues : newValues[0] || null);
    }
  };

  // Очистка выбранного значения
  const handleClear = (e) => {
    e.stopPropagation();
    setSelectedValues([]);

    if (onChange) {
      onChange(multiple ? [] : null);
    }
  };

  // Обработка изменения поискового запроса
  const handleSearchChange = (e) => {
    setSearchValue(e.target.value);
  };

  // Получение выбранных опций
  const getSelectedOptions = () => {
    return options.filter((option) => selectedValues.includes(option[valueKey]));
  };

  return (
    <div className={classNames(styles.selectContainer, className)} ref={selectRef} {...props}>
      {label && <label className={styles.label}>{label}</label>}
      <div
        className={classNames(
          styles.select,
          { [styles.disabled]: disabled },

          { [styles.open]: isOpen },
        )}
      >
        {isSearchMode && searchable ? (
          <SelectInput
            searchValue={searchValue}
            onSearchChange={handleSearchChange}
            toggleDropdown={toggleDropdown}
            arrowColor={arrowColor}
            isOpen={isOpen}
          />
        ) : (
          <SelectValue
            selectedOptions={getSelectedOptions()}
            titleKey={titleKey}
            toggleDropdown={toggleDropdown}
            handleClear={handleClear}
            placeholder={placeholder}
            renderValue={renderValue}
            multiple={multiple}
            selectedValues={selectedValues}
            valueKey={valueKey}
            clearable={clearable}
            arrowColor={arrowColor}
            isOpen={isOpen}
            disabled={disabled}
          />
        )}

        {isOpen && (
          <SelectDropdown
            position={position}
            filteredOptions={filteredOptions}
            handleOptionClick={handleOptionClick}
            selectedValues={selectedValues}
            valueKey={valueKey}
            titleKey={titleKey}
            multiple={multiple}
            renderOption={renderOption}
          />
        )}
      </div>
      {!hideErrorBlock && (
        <div className={styles.dataErrorBlock} ref={blockErrorRef}>
          {required && isFormValidate && (
            <>
              <img src={attentionIcon} alt={'attention'} />
              <div className={styles.dataError}>
                {errorMessage?.message || 'Поле обязательно для заполнения'}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default Select;
